<!--
  ClientStoresPanel.vue -- Stores section of the Client detail page.

  Reads /api/stores/?client_id=<n>. Renders each Store as a row with:
    * Name + status (Live / Draft)
    * Slug / domain hint (visible when set)

  We deliberately do NOT show order activity counts here yet -- the
  rollup endpoint to surface "12 orders this week" per-Store doesn't
  exist as a cheap aggregate. Added as a follow-up enhancement when
  that rollup ships.
-->
<template>
    <div class="csp" data-testid="client-stores-panel">
        <div v-if="loading" class="csp__loading">Loading stores...</div>
        <div v-else-if="error" class="csp__error">{{ error }}</div>
        <p v-else-if="stores.length === 0" class="csp__empty">
            This client has no stores yet.
        </p>
        <ul v-else class="csp__list">
            <li
                v-for="store in stores"
                :key="store.id"
                class="csp__item"
                :data-testid="`client-stores-row-${store.id}`"
            >
                <span class="csp__name">{{ store.name }}</span>
                <span
                    class="csp__status"
                    :class="`csp__status--${store.active ? 'live' : 'draft'}`"
                >
                    {{ store.active ? 'Live' : 'Draft' }}
                </span>
                <span v-if="store.slug" class="csp__slug">
                    {{ store.slug }}
                </span>
            </li>
        </ul>
    </div>
</template>

<script>
import api from '@/services/api'

export default {
    name: 'ClientStoresPanel',
    props: {
        clientId: { type: [Number, String], required: true },
    },
    data() {
        return {
            stores: [],
            loading: true,
            error: '',
        }
    },
    watch: {
        clientId() {
            this.load()
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            this.loading = true
            this.error = ''
            try {
                // PR-5b renamed /api/campaigns/* -> /api/stores/*; the
                // canonical URL is /stores/ (see PR-5c sweep test in
                // ``frontend/src/__tests__/campaigns-api-url-sweep.spec.js``).
                // SAFRS supports filter[client_id]=N -- standard
                // JSON:API filter shape.
                const res = await api.get(
                    `/stores/?filter[client_id]=${this.clientId}`,
                )
                const data = res?.data?.data
                if (Array.isArray(data)) {
                    this.stores = data.map((row) => ({
                        id: row.id,
                        ...(row.attributes || {}),
                    }))
                } else if (Array.isArray(res?.data?.stores)) {
                    this.stores = res.data.stores
                } else {
                    this.stores = []
                }
            } catch (e) {
                this.error = 'Could not load stores.'
                this.stores = []
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.csp__loading,
.csp__error,
.csp__empty {
    padding: 12px;
    color: var(--tf-fg-2);
    font-size: 13px;
}
.csp__error {
    color: #c0392b;
}
.csp__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.csp__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: var(--tf-surface-alt, #f7f8fa);
    border-radius: var(--tf-radius-xs, 4px);
}
.csp__name {
    flex: 1;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.csp__status {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 12px;
}
.csp__status--live {
    background: #e6f7e6;
    color: #14571c;
}
.csp__status--draft {
    background: var(--tf-bg, #fff);
    color: var(--tf-fg-3, #6b7280);
    border: 1px solid var(--tf-border, #d0d4dc);
}
.csp__slug {
    font-size: 12px;
    color: var(--tf-fg-3, #6b7280);
    font-family: monospace;
}
</style>
